import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Search, Filter, ChevronUp, ChevronDown, Phone, Play, ReceiptText, Download, CheckCheck, PhoneMissed, Voicemail, ChevronsUpDown, Check } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { DateRange } from 'react-day-picker';
import PaginationComponent from '@/components/common/PaginationComponent';
import PatientDetails from '@/components/common/PatientDetails';
import { DateRangePicker } from '@/components/ui/date-range-picker';
import { useVoicemails } from '@/hooks/voicemail/useVoicemails';
import { voicemailService } from '@/services/features/VoicemailService';
import { formatSMSDateTime, formatPhoneNumber, getDisplayValue } from '@/utils/smsUtils';
import { format } from 'date-fns';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';
import { Badge } from '../ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { useClinics } from '@/hooks/clinics/useClinics';
import { cn } from '@/lib/utils';

interface CallHistoryTabProps {
  recordsPerPage: number;
  onRecordsPerPageChange: (records: number) => void;
}

const CallHistoryTab: React.FC<CallHistoryTabProps> = ({ 
  recordsPerPage, 
  onRecordsPerPageChange 
}) => {
  // Form state (for input fields)
  const [searchTerm, setSearchTerm] = useState('');
  const [searchClinicName, setSearchClinicName] = useState('');
  const [selectedClinicId, setSelectedClinicId] = useState<number | null>(null);
  const [searchClinicNumber, setSearchClinicNumber] = useState('');
  const [searchPatientNumber, setSearchPatientNumber] = useState('');
  const [dateRange, setDateRange] = useState<DateRange | undefined>();
  const [clinicComboboxOpen, setClinicComboboxOpen] = useState(false);
  
  // Applied filters state (for actual filtering)
  const [appliedFilters, setAppliedFilters] = useState({
    searchTerm: '',
    searchClinicName: '',
    searchClinicNumber: '',
    searchPatientNumber: '',
    dateRange: undefined as DateRange | undefined
  });
  
  const [currentPage, setCurrentPage] = useState(1);
  const pendingSizeRef = useRef<number | null>(null);
  
  // Sorting state
  const [sortField, setSortField] = useState<string>('');
  const [sortDirection, setSortDirection] = useState<'ASC' | 'DESC'>('ASC');
  
  // Recording playback state
  const [isRecordingDialogOpen, setIsRecordingDialogOpen] = useState(false);
  const [currentRecordingUrl, setCurrentRecordingUrl] = useState<string>('');
  const [currentRecordingInfo, setCurrentRecordingInfo] = useState<{ patientName: string; startTime: string; timezone?: string } | null>(null);
  const [loadingRecordingId, setLoadingRecordingId] = useState<number | null>(null);
  const [downloadingRecordingId, setDownloadingRecordingId] = useState<number | null>(null);
  const [markingAsSeenId, setMarkingAsSeenId] = useState<number | null>(null);
  
  // Call summary state
  const [isSummaryDialogOpen, setIsSummaryDialogOpen] = useState(false);
  const [currentSummary, setCurrentSummary] = useState<string>('');
  const [currentSummaryInfo, setCurrentSummaryInfo] = useState<{ patientName: string; startTime: string; timezone?: string } | null>(null);
  
  const { user, token } = useAuth();
  const { toast } = useToast();

  // Use the voicemails hook
  const { data, loading, totalElements, totalPages, fetchData } = useVoicemails();

  // Use clinics hook for searchable dropdown
  const { clinics, loading: clinicsLoading, searchClinics } = useClinics(10, 300);

  // Check if any filters have been changed from initial state
  const hasFilterChanges = searchTerm !== '' || searchClinicName !== '' || selectedClinicId !== null ||
    searchClinicNumber !== '' || searchPatientNumber !== '' || dateRange !== undefined;

  // Handle sorting
  const handleSort = (field: string) => {
    let newSortDirection: 'ASC' | 'DESC' = 'ASC';
    
    if (sortField === field) {
      newSortDirection = sortDirection === 'ASC' ? 'DESC' : 'ASC';
      setSortDirection(newSortDirection);
    } else {
      setSortField(field);
      setSortDirection(newSortDirection);
    }

    // Trigger fetch with new sort
    const filters = {
      patientName: appliedFilters.searchTerm,
      clinicName: appliedFilters.searchClinicName,
      clinicNumber: appliedFilters.searchClinicNumber,
      patientNumber: appliedFilters.searchPatientNumber,
      startDate: appliedFilters.dateRange?.from ? format(appliedFilters.dateRange.from, 'yyyy-MM-dd') : undefined,
      endDate: appliedFilters.dateRange?.to ? format(appliedFilters.dateRange.to, 'yyyy-MM-dd') : undefined,
      page: currentPage - 1,
      size: recordsPerPage,
      sort: `${newSortDirection}`
    };

    fetchData(filters);
  };

  // Render sortable header helper
  const renderSortableHeader = (label: string, field: string) => {
    const isActive = sortField === field;
    
    return (
      <TableHead 
        className="cursor-pointer select-none hover:bg-muted/50"
        onClick={() => handleSort(field)}
      >
        <div className="flex items-center gap-1">
          {label}
          {isActive && (
            sortDirection === 'ASC' ? 
              <ChevronUp className="h-4 w-4" /> : 
              <ChevronDown className="h-4 w-4" />
          )}
        </div>
      </TableHead>
    );
  };

  // Memoize filters object
  const filters = useMemo(() => ({
    patientName: appliedFilters.searchTerm,
    clinicName: appliedFilters.searchClinicName,
    clinicNumber: appliedFilters.searchClinicNumber,
    patientNumber: appliedFilters.searchPatientNumber,
    startDate: appliedFilters.dateRange?.from ? format(appliedFilters.dateRange.from, 'yyyy-MM-dd') : undefined,
    endDate: appliedFilters.dateRange?.to ? format(appliedFilters.dateRange.to, 'yyyy-MM-dd') : undefined,
    page: currentPage - 1,
    size: recordsPerPage,
    sort: sortField ? `${sortDirection}` : undefined
  }), [appliedFilters, currentPage, recordsPerPage, sortField, sortDirection]);

  // Fetch data when filters change
  useEffect(() => {
    fetchData(filters);
  }, [filters, fetchData]);

  // Handle search button click
  const handleSearch = () => {
    // Apply current form values as filters
    setAppliedFilters({
      searchTerm,
      searchClinicName,
      searchClinicNumber,
      searchPatientNumber,
      dateRange
    });
    
    // Reset to page 1 when searching
    setCurrentPage(1);
  };

  // Handle page change
  const handlePageChange = (page: number) => {
    // Check if there's a pending size change
    if (pendingSizeRef.current !== null) {
      // Use the pending size for the fetch
      const newSize = pendingSizeRef.current;
      pendingSizeRef.current = null; // Clear pending size
      
      setCurrentPage(page);
      onRecordsPerPageChange(newSize);
    } else {
      setCurrentPage(page);
    }
  };

  const handleRecordsPerPageChange = (records: number) => {
    // Store the pending size change
    pendingSizeRef.current = records;
    
    // Reset to page 1 when changing records per page
    handlePageChange(1);
  };

  const clearFilters = () => {
    // Clear form inputs
    setSearchTerm('');
    setSearchClinicName('');
    setSelectedClinicId(null);
    setSearchClinicNumber('');
    setSearchPatientNumber('');
    setDateRange(undefined);
    
    // Clear applied filters
    setAppliedFilters({
      searchTerm: '',
      searchClinicName: '',
      searchClinicNumber: '',
      searchPatientNumber: '',
      dateRange: undefined
    });
    
    // Reset to page 1
    setCurrentPage(1);
  };

  // Calculate display values for pagination
  const totalRecords = totalElements;
  const startIndex = totalRecords > 0 ? (currentPage - 1) * recordsPerPage + 1 : 0;
  const endIndex = Math.min((currentPage - 1) * recordsPerPage + recordsPerPage, totalRecords);

  const getCallTypeClassName = (type: string) => {
    return type === 'Missed' ? 'bg-red-50 text-red-600 border-red-200 hover:bg-red-100 hover:text-red-700' : 'bg-amber-50 text-amber-600 border-amber-200 hover:bg-amber-100 hover:text-amber-700';
  };

  const handlePlayRecording = async (s3Key: string, logId: number, patientName: string, startTime: string, timezone?: string) => {
    if (!s3Key) {
      toast({
        title: "Error",
        description: "No recording available for this call.",
        variant: "destructive",
        duration: 3000
      });
      return;
    }

    if (!user?.id || !token) {
      toast({
        title: "Error",
        description: "User not authenticated",
        variant: "destructive",
        duration: 3000
      });
      return;
    }

    setLoadingRecordingId(logId);
    try {
      const response = await voicemailService.getRecordingPresignedUrl(
        user.id.toString(),
        s3Key,
        token
      );

      if (response.url) {
        setCurrentRecordingUrl(response.url);
        setCurrentRecordingInfo({ patientName, startTime, timezone });
        setIsRecordingDialogOpen(true);
      } else {
        toast({
          title: "Error",
          description: "No recording available for this call.",
          variant: "destructive",
          duration: 3000
        });
      }
    } catch (error) {
      console.error('Error getting recording URL:', error);
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to load recording",
        variant: "destructive",
        duration: 3000
      });
    } finally {
      setLoadingRecordingId(null);
    }
  };

  const handleDownloadRecording = async (s3Key: string, logId: number) => {
    if (!s3Key) {
      toast({
        title: "Error",
        description: "No recording available for this call.",
        variant: "destructive",
        duration: 3000
      });
      return;
    }

    if (!user?.id || !token) {
      toast({
        title: "Error",
        description: "User not authenticated",
        variant: "destructive",
        duration: 3000
      });
      return;
    }

    setDownloadingRecordingId(logId);
    try {
      const response = await voicemailService.getRecordingPresignedUrl(
        user.id.toString(),
        s3Key,
        token
      );

      if (response.url) {
        window.open(response.url, '_blank');
        toast({
          title: "Success",
          description: "Recording download started",
          duration: 2000
        });
      } else {
        toast({
          title: "Error",
          description: "No recording available for this call.",
          variant: "destructive",
          duration: 3000
        });
      }
    } catch (error) {
      console.error('Error getting recording URL:', error);
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to download recording",
        variant: "destructive",
        duration: 3000
      });
    } finally {
      setDownloadingRecordingId(null);
    }
  };

  const handleCloseRecordingDialog = () => {
    setIsRecordingDialogOpen(false);
    setCurrentRecordingUrl('');
    setCurrentRecordingInfo(null);
  };

  const handleViewSummary = (summary: string, patientName: string, startTime: string, timezone?: string) => {
    if (!summary) {
      toast({
        title: "No Summary",
        description: "No call summary available for this call.",
        variant: "destructive",
        duration: 3000
      });
      return;
    }
    setCurrentSummary(summary);
    setCurrentSummaryInfo({ patientName, startTime, timezone });
    setIsSummaryDialogOpen(true);
  };

  const handleCloseSummaryDialog = () => {
    setIsSummaryDialogOpen(false);
    setCurrentSummary('');
    setCurrentSummaryInfo(null);
  };

  const handleMarkAsSeen = async (logId: number) => {
    if (!user?.id || !token) {
      toast({
        title: "Error",
        description: "User not authenticated",
        variant: "destructive",
        duration: 3000
      });
      return;
    }

    setMarkingAsSeenId(logId);
    try {
      await voicemailService.markVoicemailAsRead(
        logId,
        user.id.toString(),
        token
      );

      toast({
        title: "Success",
        description: "Voicemail marked as seen",
        duration: 2000
      });

      // Refetch the data to update the list
      handleSearch();
    } catch (error) {
      console.error('Error marking voicemail as seen:', error);
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to mark voicemail as seen",
        variant: "destructive",
        duration: 3000
      });
    } finally {
      setMarkingAsSeenId(null);
    }
  };

  return (
    <div className="space-y-4">
      {/* Search and Filters */}
      <div id="call_history_filters_container" className="bg-card rounded-lg border p-4">
        <div className="flex items-center gap-2 mb-4">
          <Filter className="h-5 w-5 text-muted-foreground" />
          <h3 className="font-semibold">Search & Filters</h3>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <div>
            <label className="text-sm font-medium mb-1 block">Patient Name</label>
            <Input
              id="call_history_patient_name_input"
              placeholder="Search by patient name"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
            />
          </div>
          
          <div>
            <label className="text-sm font-medium mb-1 block">Clinic Name</label>
            <Popover open={clinicComboboxOpen} onOpenChange={setClinicComboboxOpen}>
              <PopoverTrigger asChild>
                <Button
                  id="call_history_clinic_select_btn"
                  variant="outline"
                  role="combobox"
                  aria-expanded={clinicComboboxOpen}
                  className="w-full justify-between font-normal"
                >
                  {selectedClinicId
                    ? clinics.find((clinic) => clinic.clinic_id === selectedClinicId)?.clinic_name
                    : "Search by clinic name"}
                  <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent id="call_history_clinic_popover" className="w-[--radix-popover-trigger-width] p-0" align="start">
                <Command>
                  <CommandInput 
                    placeholder="Search clinic..." 
                    onValueChange={(value) => {
                      searchClinics(value);
                    }}
                  />
                  <CommandList>
                    <CommandEmpty>
                      {clinicsLoading ? 'Loading clinics...' : 'No clinic found.'}
                    </CommandEmpty>
                    <CommandGroup>
                      {clinics.map((clinic) => (
                        <CommandItem
                          key={clinic.clinic_id}
                          value={clinic.clinic_name}
                          onSelect={() => {
                            setSelectedClinicId(clinic.clinic_id);
                            setSearchClinicName(clinic.clinic_name);
                            setClinicComboboxOpen(false);
                          }}
                        >
                          <Check
                            className={cn(
                              "mr-2 h-4 w-4",
                              selectedClinicId === clinic.clinic_id ? "opacity-100" : "opacity-0"
                            )}
                          />
                          {clinic.clinic_name}
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
          </div>

          <div>
            <label className="text-sm font-medium mb-1 block">Clinic Number</label>
            <Input
              id="call_history_clinic_number_input"
              placeholder="Search by clinic number"
              value={searchClinicNumber}
              onChange={(e) => setSearchClinicNumber(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
            />
          </div>

          <div>
            <label className="text-sm font-medium mb-1 block">Patient Number</label>
            <Input
              id="call_history_patient_number_input"
              placeholder="Search by patient number"
              value={searchPatientNumber}
              onChange={(e) => setSearchPatientNumber(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
            />
          </div>
          
          <div>
            <label className="text-sm font-medium mb-1 block">Date Range</label>
            <DateRangePicker
              dateRange={dateRange}
              onDateRangeChange={setDateRange}
            />
          </div>
        </div>
        
        <div className="flex gap-2 mt-4">
          <Button 
            id="call_history_search_btn"
            onClick={handleSearch}
            className="flex items-center gap-2"
          >
            <Search className="h-4 w-4" />
            Search
          </Button>
          {hasFilterChanges && (
            <Button 
              id="call_history_clear_filters_btn"
              variant="outline"
              onClick={clearFilters}
            >
              Clear Filters
            </Button>
          )}
        </div>
      </div>

      {/* Results Summary */}
      <div className="flex items-center justify-between">
        <div className="text-sm text-muted-foreground">
          {loading ? (
            'Loading...'
          ) : (
            `Showing ${startIndex + 1}-${endIndex} of ${totalRecords} call records`
          )}
        </div>
      </div>

      {/* Table */}
      <div id="call_history_table_container" className="border rounded-lg">
        <Table id="table-call-history">
          <TableHeader id="table-call-history-header">
            <TableRow>
              <TableHead id="header-cell-patient-details">Patient Details</TableHead>
              <TableHead id="header-cell-clinic-number">Clinic Number</TableHead>
              <TableHead id="header-cell-call-type">Call type</TableHead>
              <TableHead id="header-cell-duration">Duration</TableHead>
              {renderSortableHeader('Date & Time', 'createdAt')}
              <TableHead id="header-cell-action">Action</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody id="table-call-history-body">
            {loading ? (
              <TableRow>
                <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">
                  Loading call history...
                </TableCell>
              </TableRow>
            ) : data.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">
                  No call history records found
                </TableCell>
              </TableRow>
            ) : (
              data.map((record) => (
                <TableRow key={record.logId}>
                  <TableCell>
                    {record.patientName && record.patientName.trim() ? (
                      <PatientDetails
                        patientName={record.patientName}
                        patientId={record.patientId?.toString()}
                        patientClinicDataId={record.patientId}
                        emrId={record.emr}
                        gender={record.gender}
                        dateOfBirth={record.birthDate}
                        phoneNumber={record.patientNumber}
                        callButtonVariant="neutral"
                        hideCallButton={true}
                      />
                    ) : (
                      <div className="text-muted-foreground">Unknown Patient</div>
                    )}
                  </TableCell>
                  <TableCell>
                    <div className="space-y-1">
                      <div className="text-sm font-medium">
                        {getDisplayValue(record.hccName)}
                      </div>
                      <div className="font-mono text-sm text-muted-foreground">
                        {formatPhoneNumber(record.clinicNumber)}
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge className={`${getCallTypeClassName(record.callType)} py-1`}>
                      {record.callType == 'Missed' && <PhoneMissed size={14} className='mr-2' />}
                      {record.callType == 'Voicemail' && <Voicemail size={14} className='mr-2' />}
                      {record.callType}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    {record.duration}
                  </TableCell>
                  <TableCell>
                    <div className="text-sm">
                      {formatSMSDateTime(record.startTime, record.timezone)}
                      {record.timezone && (
                        <div className="text-xs text-muted-foreground mt-1">
                          {record.timezone}
                        </div>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex space-x-2">
                    <TooltipProvider delayDuration={0}>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            variant="outline"
                            size="icon"
                            className="h-10 w-10 border-pink-200 bg-pink-50 hover:bg-pink-100 hover:border-pink-300 transition-all duration-200 rounded-lg shadow-sm"
                            onClick={() => handlePlayRecording(record.s3Key || '', record.logId, record.patientName, record.startTime, record.timezone)}
                            disabled={!record.s3Key || loadingRecordingId === record.logId}
                          >
                            {loadingRecordingId === record.logId ? (
                              <div className="h-4 w-4 border-2 border-pink-600 border-t-transparent rounded-full animate-spin" />
                            ) : (
                              <Play className="h-4 w-4 text-pink-600" />
                            )}
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent className='text-xs shadow-lg'>
                          <p>Play Call Recording</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                    <TooltipProvider delayDuration={0}>
                      <Tooltip>
                        <TooltipTrigger asChild>
                           <Button
                            variant="outline"
                            size="icon"
                            className="h-10 w-10 border-blue-200 bg-blue-50 hover:bg-blue-100 hover:border-blue-300 transition-all duration-200 rounded-lg shadow-sm"
                            onClick={() => handleViewSummary(record.summary || '', record.patientName, record.startTime, record.timezone)}
                            disabled={!record.summary}
                          >
                            <ReceiptText className="h-4 w-4 text-blue-600" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent className='text-xs shadow-lg'>
                          <p>{!record.summary ? 'No summary available' : 'View Call Summary'}</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                    <TooltipProvider delayDuration={0}>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            variant="outline"
                            size="icon"
                            className="h-10 w-10 border-purple-200 bg-purple-50 hover:bg-purple-100 hover:border-purple-300 transition-all duration-200 rounded-lg shadow-sm pointer-events-auto"
                            onClick={() => handleDownloadRecording(record.s3Key || '', record.logId)}
                            disabled={!record.s3Key || downloadingRecordingId === record.logId}
                          >
                            {downloadingRecordingId === record.logId ? (
                              <div className="h-4 w-4 border-2 border-purple-600 border-t-transparent rounded-full animate-spin" />
                            ) : (
                              <Download className="h-4 w-4 text-purple-600" />
                            )}
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent className='text-xs shadow-lg'>
                          <p>{!record.s3Key ? 'No recording available' : 'Download Call Recording'}</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                    <TooltipProvider delayDuration={0}>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            variant="outline"
                            size="icon"
                            className="h-10 w-10 border-green-200 bg-green-50 hover:bg-green-100 hover:border-green-300 transition-all duration-200 rounded-lg shadow-sm"
                            onClick={() => handleMarkAsSeen(record.logId)}
                            disabled={markingAsSeenId === record.logId}
                          >
                            {markingAsSeenId === record.logId ? (
                              <div className="h-4 w-4 border-2 border-green-600 border-t-transparent rounded-full animate-spin" />
                            ) : (
                              <CheckCheck className="h-4 w-4 text-green-600" />
                            )}
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent className='text-xs shadow-lg'>
                          <p>Mark as Seen</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Pagination */}
      {totalRecords > 0 && (
        <PaginationComponent
          currentPage={currentPage}
          totalPages={totalPages}
          recordsPerPage={recordsPerPage}
          totalRecords={totalRecords}
          startIndex={startIndex}
          endIndex={endIndex}
          onPageChange={handlePageChange}
          onRecordsPerPageChange={handleRecordsPerPageChange}
        />
      )}

      {/* Recording Playback Dialog */}
      <Dialog open={isRecordingDialogOpen} onOpenChange={handleCloseRecordingDialog}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle>Call Recording</DialogTitle>
            <DialogDescription>
              {currentRecordingInfo && (
                <div className="space-y-1 text-sm">
                  <div><span className="font-medium">Patient:</span> {currentRecordingInfo.patientName}</div>
                  <div>
                    <span className="font-medium">Date: </span>
                    {formatSMSDateTime(currentRecordingInfo.startTime, currentRecordingInfo.timezone || '')}
                    {currentRecordingInfo.timezone && (
                      <span className="text-xs text-muted-foreground ml-1">({currentRecordingInfo.timezone})</span>
                    )}
                  </div>
                </div>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            {currentRecordingUrl ? (
              <audio 
                controls 
                className="w-full"
                autoPlay={false}
                preload="metadata"
              >
                <source src={currentRecordingUrl} type="audio/mpeg" />
                <source src={currentRecordingUrl} type="audio/mp3" />
                Your browser does not support the audio element.
              </audio>
            ) : (
              <div className="text-center text-muted-foreground py-8">
                No recording available
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Call Summary Dialog */}
      <Dialog open={isSummaryDialogOpen} onOpenChange={handleCloseSummaryDialog}>
        <DialogContent className="sm:max-w-[700px]">
          <DialogHeader>
            <DialogTitle>Call Summary</DialogTitle>
            <DialogDescription>
              {currentSummaryInfo && (
                <div className="space-y-1 text-sm">
                  <div><span className="font-medium">Patient:</span> {currentSummaryInfo.patientName}</div>
                  <div>
                    <span className="font-medium">Date: </span>
                    {formatSMSDateTime(currentSummaryInfo.startTime, currentSummaryInfo.timezone || '')}
                    {currentSummaryInfo.timezone && (
                      <span className="text-xs text-muted-foreground ml-1">({currentSummaryInfo.timezone})</span>
                    )}
                  </div>
                </div>
              )}
            </DialogDescription>
          </DialogHeader>
          <div>
            {currentSummary ? (
              <div className="bg-muted/30 rounded-lg p-4 border">
                <p className="text-sm leading-relaxed whitespace-pre-wrap">
                  {currentSummary}
                </p>
              </div>
            ) : (
              <div className="text-center text-muted-foreground py-8">
                No summary available
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CallHistoryTab;
